/**
 * @fileoverview PodChatSheet — wires the generic `ChatSheet` to the POD
 * adjudication chat backend (MOR-879).
 *
 * Mirrors `ContactChatSheet` but threads on `(jobId, userId)` and sends
 * through `api.jobs.recoveries.chat.sendPodChatMessage`. The agent has the
 * whole POD schedule — claims, reconciliation figures, adjudication
 * outcomes — baked into its system prompt, so the suggested prompts in the
 * empty state revolve around that surface.
 *
 * Used by:
 * - src/components/jobs/phases/recoveries/RecoveriesView.tsx
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexAction } from "@convex-dev/react-query";
import { useThreadMessages } from "@convex-dev/agent/react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChatSheet,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";

interface PodChatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: Id<"jobs">;
}

export function PodChatSheet({ open, onOpenChange, jobId }: PodChatSheetProps) {
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isNewThread, setIsNewThread] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  // Reset thread state whenever the panel switches jobs so we don't carry
  // a stale thread from job A across to job B.
  useEffect(() => {
    setActiveThreadId(null);
    setIsNewThread(false);
    setPendingMessage(null);
    setWaitingForResponse(false);
  }, [jobId]);

  // Current user — for the avatar in the chat input and message bubbles.
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: currentUser.avatarUrl,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
  }, [currentUser]);

  // List threads for this (job × user). Newest first.
  const { data: threadList } = useQuery(
    convexQuery(api.jobs.recoveries.chatInternal.listPodChatThreads, {
      jobId,
    }),
  );

  // Auto-select the most recent thread on open unless the user
  // explicitly clicked "New".
  useEffect(() => {
    if (
      !activeThreadId &&
      !isNewThread &&
      threadList &&
      threadList.length > 0
    ) {
      setActiveThreadId(threadList[0].threadId);
    }
  }, [activeThreadId, isNewThread, threadList]);

  // Subscribe to messages on the active thread (with streaming deltas).
  const threadMessages = useThreadMessages(
    api.jobs.recoveries.chatInternal.listPodChatMessages,
    activeThreadId ? { threadId: activeThreadId } : "skip",
    { initialNumItems: 50, stream: true },
  );

  type RawAgentMessage = {
    _id?: string;
    _creationTime: number;
    key?: string;
    streaming?: boolean;
    message?: {
      role?: string;
      content?:
        string | Array<{ type?: string; text?: string; toolName?: string }>;
    };
  };

  const realMessages: ChatMessageData[] = (
    (threadMessages?.results ?? []) as Array<RawAgentMessage>
  )
    .filter((m) => {
      if (m.message?.role !== "user" && m.message?.role !== "assistant")
        return false;
      const content = m.message?.content;
      if (!content) return false;
      if (typeof content === "string") return content.trim().length > 0;
      if (Array.isArray(content)) {
        return content.some(
          (part) => part.type === "text" && part.text?.trim(),
        );
      }
      return false;
    })
    .map((m) => {
      const content = m.message?.content ?? "";
      const text =
        typeof content === "string"
          ? content
          : content
              .filter((p) => p.type === "text")
              .map((p) => p.text ?? "")
              .join("");

      return {
        id: m.key ?? m._id ?? String(m._creationTime),
        role: (m.message?.role ?? "assistant") as "user" | "assistant",
        content: text,
        createdAt: m._creationTime,
        isStreaming: m.streaming === true,
      };
    });

  // Once the optimistic user message arrives in the real stream, drop it.
  useEffect(() => {
    if (
      pendingMessage &&
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      return realMessages;
    }
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  // Derive thinking + tool-call state from raw messages — same logic as
  // SummaryChatSheet so the typing indicator behaves identically.
  const { isAgentThinking, activeToolCalls } = useMemo(() => {
    const raw = (threadMessages?.results ?? []) as Array<RawAgentMessage>;
    if (raw.length === 0) {
      return {
        isAgentThinking: waitingForResponse,
        activeToolCalls: [] as string[],
      };
    }

    const tools: string[] = [];
    for (let i = raw.length - 1; i >= 0; i--) {
      const m = raw[i];
      if (m.message?.role === "user") break;
      if (m.message?.role === "assistant" && Array.isArray(m.message.content)) {
        for (const part of m.message.content) {
          const tc = part as { type?: string; toolName?: string };
          if (tc.type === "tool-call" && tc.toolName) {
            tools.push(tc.toolName);
          }
        }
      }
    }

    const lastRaw = raw[raw.length - 1];
    const lastRole = lastRaw.message?.role;

    if (lastRaw.streaming && lastRole === "assistant") {
      return { isAgentThinking: false, activeToolCalls: [] as string[] };
    }

    const hasToolCallParts =
      lastRole === "assistant" &&
      Array.isArray(lastRaw.message?.content) &&
      (lastRaw.message?.content as Array<{ type?: string }>).some(
        (p) => p.type === "tool-call",
      );

    const isWorking =
      waitingForResponse || lastRole === "tool" || hasToolCallParts;

    return {
      isAgentThinking: isWorking,
      activeToolCalls: isWorking ? tools : [],
    };
  }, [threadMessages?.results, waitingForResponse]);

  // Clear waitingForResponse once the agent reports it isn't thinking
  // anymore (otherwise it would stay sticky across conversations).
  useEffect(() => {
    if (waitingForResponse && !isAgentThinking) {
      setWaitingForResponse(false);
    }
  }, [waitingForResponse, isAgentThinking]);

  // Send a message — fires the public action which schedules streaming
  // on a thread it creates / reuses.
  const { mutateAsync: sendAction } = useMutation({
    mutationFn: useConvexAction(api.jobs.recoveries.chat.sendPodChatMessage),
  });

  const handleSend = useCallback(
    async (message: string) => {
      setPendingMessage(message);
      setWaitingForResponse(true);
      setIsSending(true);
      try {
        const result = await sendAction({
          jobId,
          message,
          threadId: activeThreadId ?? undefined,
        });
        if (!activeThreadId) {
          setActiveThreadId(result.threadId);
          setIsNewThread(false);
        }
      } catch (err) {
        const friendly = extractErrorMessage(
          err,
          "Could not send your message — please try again.",
        );
        console.error("[PodChatSheet] Failed to send message:", err);
        setPendingMessage(null);
        setWaitingForResponse(false);
        toast.error(friendly);
      } finally {
        setTimeout(() => setIsSending(false), 500);
      }
    },
    [sendAction, jobId, activeThreadId],
  );

  const handleNewThread = useCallback(() => {
    setActiveThreadId(null);
    setIsNewThread(true);
  }, []);

  const handleSelectThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    setIsNewThread(false);
  }, []);

  const threads = useMemo(
    () =>
      (threadList ?? []).map((t) => ({
        threadId: t.threadId,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
        preview: t.preview,
      })),
    [threadList],
  );

  const emptyMessage =
    "Ask Morgan about proof of debts (e.g. which claims are missing supporting documentation and which claims are waiting on adjudication)";

  return (
    <ChatSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Chat — Proof of Debt"
      description="Ask Morgan about the POD schedule, substantiation, and adjudication outcomes."
      messages={messages}
      onSend={handleSend}
      userInfo={userInfo}
      isSending={isSending}
      isThinking={isAgentThinking}
      activeToolCalls={activeToolCalls}
      emptyMessage={emptyMessage}
      inputPlaceholder="Ask about the POD claims…"
      threads={threads}
      activeThreadId={activeThreadId}
      onNewThread={handleNewThread}
      onSelectThread={handleSelectThread}
    />
  );
}
